import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import Link from "@tiptap/extension-link";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";

import { InsertLinkDialog } from "./InsertLinkDialog";
import { RichTextToolbar } from "./RichTextToolbar";

export interface RichTextFieldProps {
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
    minHeight?: number;
    // MUI standard props
    id?: string;
    name?: string;
    label?: string;
    helperText?: string;
    error?: boolean;
    disabled?: boolean;
    required?: boolean;
    fullWidth?: boolean;
    className?: string;
    // Event handlers
    onBlur?: () => void;
    onFocus?: () => void;
}

export const RichTextField = forwardRef<HTMLDivElement, RichTextFieldProps>(
    (
        {
            value,
            onChange,
            placeholder,
            minHeight = 160,
            label,
            helperText,
            error = false,
            disabled = false,
            required = false,
            fullWidth = false,
            className,
            onBlur,
            onFocus,
        },
        ref,
    ) => {
        const [linkDialogOpen, setLinkDialogOpen] = useState(false);
        const editorRef = useRef<HTMLDivElement>(null);

        useImperativeHandle(ref, () => editorRef.current!, []);

        const editor = useEditor({
            extensions: [
                StarterKit.configure({
                    heading: { levels: [2, 3, 4] },
                    blockquote: false,
                    codeBlock: false,
                    code: false,
                    strike: false,
                    horizontalRule: false,
                    dropcursor: { width: 2, color: "#1976d2" },
                    gapcursor: false,
                    link: false,
                }),
                Link.configure({
                    openOnClick: false,
                    autolink: true,
                    HTMLAttributes: { rel: "nofollow noopener noreferrer" },
                }),
            ],
            content: value,
            onUpdate: ({ editor }) => onChange(editor.getHTML()),
            editable: !disabled,
            immediatelyRender: true,
            shouldRerenderOnTransaction: false,
            editorProps: {
                attributes: {
                    spellcheck: "true",
                    style: `min-height:${minHeight}px;padding:8px;border:none;outline:none;background:transparent;`,
                    placeholder: placeholder || "",
                },
                handleDOMEvents: {
                    focus: () => {
                        onFocus?.();
                        return false;
                    },
                    blur: () => {
                        onBlur?.();
                        return false;
                    },
                },
            },
        });

        useEffect(() => {
            if (!editor) return;
            const currentHtml = editor.getHTML();
            if (value !== currentHtml) {
                editor.commands.setContent(value);
            }
        }, [value, editor]);

        useEffect(() => {
            if (!editor) return;
            editor.setEditable(!disabled);
        }, [disabled, editor]);

        function handleLinkDialogClose() {
            setLinkDialogOpen(false);
        }

        if (!editor) return null;

        const hasLabel = Boolean(label);
        const hasHelperText = Boolean(helperText);

        const editorContent = (
            <Box
                ref={editorRef}
                sx={{
                    position: "relative",
                    border: "1px solid",
                    borderColor: error ? "error.main" : "divider",
                    borderRadius: 1,
                    bgcolor: disabled ? "action.disabledBackground" : "paper",
                    "&:hover": {
                        borderColor: error
                            ? "error.main"
                            : disabled
                              ? "divider"
                              : "text.primary",
                    },
                    "&:focus-within": {
                        border: "2px solid",
                        borderColor: error
                            ? "error.main"
                            : disabled
                              ? "divider"
                              : "primary.main",
                    },
                    "& a": {
                        color: "primary.main",
                        textUnderlineOffset: 4,
                    },
                    "& a:hover": {
                        textDecoration: "none",
                    },
                    transition: (theme) =>
                        theme.transitions.create(["border-color"], {
                            duration: theme.transitions.duration.short,
                        }),
                }}
                className={className}
            >
                <RichTextToolbar
                    editor={editor}
                    disabled={disabled}
                    onLinkClick={() => {
                        if (editor) {
                            const { from, to } = editor.state.selection;
                            const hasTextSelected = from !== to;
                            const linkActive = editor.isActive("link");
                            if (hasTextSelected || linkActive) {
                                setLinkDialogOpen(true);
                            }
                        }
                    }}
                />
                <EditorContent
                    editor={editor}
                    style={{
                        opacity: disabled ? 0.5 : 1,
                        cursor: disabled ? "not-allowed" : "text",
                        padding: "0 18px",
                    }}
                    placeholder={placeholder}
                />
            </Box>
        );

        return (
            <>
                {hasLabel || hasHelperText ? (
                    <FormControl
                        fullWidth={fullWidth}
                        error={error}
                        required={required}
                        disabled={disabled}
                    >
                        {hasLabel && (
                            <InputLabel
                                shrink
                                sx={{
                                    position: "static",
                                    transform: "none",
                                    mb: 1,
                                    fontSize: "0.875rem",
                                    fontWeight: 400,
                                }}
                            >
                                {label}
                            </InputLabel>
                        )}
                        {editorContent}
                        {hasHelperText && (
                            <FormHelperText sx={{ mt: 0.5 }}>
                                {helperText}
                            </FormHelperText>
                        )}
                    </FormControl>
                ) : (
                    editorContent
                )}
                <InsertLinkDialog
                    open={linkDialogOpen}
                    editor={editor}
                    onClose={handleLinkDialogClose}
                />
            </>
        );
    },
);
